import { chain } from 'lodash';
import { useMemo } from 'react';
import { Columns, FilterOption, FilterOptions } from '~components/organization/ZteTable/types';
import { ZteEnrolmentStatus, ZteFilters } from '~graphql/__generated__/types';

export function useFilterOptions(
  filters: Array<{ id: string; value: FilterOption[] }>,
  dataWithFilters: ZteFilters,
): FilterOptions {
  const filterMap = new Map(filters.map(({ id, value }) => [id, value]));

  const enabledBatchNameFilters: FilterOption[] = filterMap.get('batchName') ?? [];
  const enabledCustomerNameFilters: FilterOption[] = filterMap.get('customerName') ?? [];
  const enabledModelNumberFilters: FilterOption[] = filterMap.get('modelNumber') ?? [];
  const enabledStatusFilters: FilterOption[] = filterMap.get('status') ?? [];

  const batchNameFilters = useMemo<FilterOption[]>(() => {
    return chain(dataWithFilters.batches)
      .sortBy((g) => g.name.toLowerCase())
      .map(
        (batches): FilterOption => ({
          value: batches.name || '',
          label: batches.name || '',
          column: Columns.Batchname,
        }),
      )
      .value();
  }, [dataWithFilters]);

  const customerNameFilters = useMemo<FilterOption[]>(() => {
    return chain(dataWithFilters.customers)
      .sortBy((l) => l.name.toLowerCase())
      .map(
        (customers): FilterOption => ({
          value: customers.name || '',
          label: customers.name || '',
          column: Columns.CustomerName,
        }),
      )
      .value();
  }, [dataWithFilters]);

  const statusFilters = useMemo<FilterOption[]>(() => {
    return [
      {
        value: ZteEnrolmentStatus.Claimed,
        label: ZteEnrolmentStatus.Claimed,
        column: Columns.Status,
      },
      {
        value: ZteEnrolmentStatus.Paired,
        label: ZteEnrolmentStatus.Paired,
        column: Columns.Status,
      },
      {
        value: ZteEnrolmentStatus.Unpaired,
        label: ZteEnrolmentStatus.Unpaired,
        column: Columns.Status,
      },
    ];
  }, []);

  const modelNoFilters = useMemo<FilterOption[]>(() => {
    return chain(dataWithFilters.modelNumbers)
      .sortBy((l) => l.toLowerCase())
      .map(
        (modelNumber): FilterOption => ({
          value: modelNumber || '',
          label: modelNumber || '',
          column: Columns.ModelNumber,
        }),
      )
      .value();
  }, [dataWithFilters]);

  return {
    [Columns.Batchname]: {
      all: batchNameFilters,
      enabled: enabledBatchNameFilters,
    },
    [Columns.CustomerName]: {
      all: customerNameFilters,
      enabled: enabledCustomerNameFilters,
    },
    [Columns.Status]: {
      all: statusFilters,
      enabled: enabledStatusFilters,
    },
    [Columns.ModelNumber]: {
      all: modelNoFilters,
      enabled: enabledModelNumberFilters,
    },
  };
}
